import React from "react";
import { connect } from "react-redux";
import styled, { withTheme } from "styled-components";
import { t } from "../../../../../core/translate";
import { Button } from "@vitalsource/vstui";
import { Warning } from "@vitalsource/vstui-icons";
import { cloud } from "@vitalsource/vstui/colors";
import { AccordionContent } from "../../../../../components/accordion";
import {
  StyledAccordion,
  StyledAccordionButton,
  StyledCaret
} from "../../../styles";
import { selectors as booksSelectors } from "../../../../../../redux/books";
import {
  selectors as citationSelectors,
  actions as citationActions
} from "../../../../../../redux/citations";
import {
  selectors as overleafSelectors,
  actions as overleafActions
} from "../../../../../../redux/overleaf";
import Footer from "../footer";
import {
  actions as highlightActions,
  selectors as highlightSelectors
} from "../../../../../../redux/highlights";
import { toastUtils } from "../../../../../../utils/toast-utils";
import { OffScreenStyles } from "../../../../../components/mixins";
import { expirationMessage } from "../../../expiration-message";
import AvailabilityMessage from "../../../book/simplepanel/availability-message";
import { selectors as brandSelectors } from "../../../../../../redux/brand";

const DetailsContainerOuter = styled.div`
  height: 100%;
`;

const ScrollingContainer = styled.div`
  overflow: auto;
  height: calc(100% - ${props => (props.hasFooter ? "100px" : "15px")});
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  top: 15px;
`;

const BookDetails = styled.div`
  background: #ffffff;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 14px;
  width: 365px;
  margin: 0 auto;
  .detail-container {
    margin-bottom: 10px;
    &:last-child {
      margin-bottom: 0;
    }
    .detail {
      display: block;
      font-weight: 700;
    }
    .detail-output {
      font-weight: 400;
    }

    &.expiration {
      display: flex;
      align-items: center;
      color: ${cloud.getShade(9)};
      svg {
        margin-right: 6px;
      }
    }
  }
`;

const BookCopyURL = styled.div`
  background: #ffffff;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
  border-radius: 4px;
`;

const BookCopyContainer = styled.div`
  margin: 10px auto;
  width: 365px;
`;

const BookCitationContainer = styled(BookCopyContainer)``;

const CopyURLText = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid ${cloud.getShade(3)};
`;

const CopyURLHeader = styled.h3`
  margin: 25px 0 10px 0;
`;

const CitationHeader = styled(CopyURLHeader)``;

const CitationContent = styled.div`
  border-top: 1px solid ${cloud.getShade(3)};
  padding: 15px;
`;

const WarningMessage = styled.div`
  margin-top: 15px;
  span {
    font-style: italic;
    font-size: 1.4rem;
    color: ${cloud.getShade(10)};
    padding-left: 10px;
  }
`;

const WarningIcon = styled(Warning).attrs({
  "aria-hidden": "false",
  role: "img",
  "aria-label": t("base.warning", "Warning")
})`
  height: 13px;
  width: 15px;
`;

const PanelHeadingTag = styled.h3`
  ${OffScreenStyles};
`;

class BookDetailsContainer extends React.Component {
  componentDidMount() {
    this.props.getCitationInfo();
    this.props.fetchDecks({ isbn: this.props.isbn });
    this.props.fetchHighlightsSummary();
  }

  copyLink = () => {
    this.copyTextAndNotify(this.props.url, t("sharing.link-copied"));
  };

  copyCitation = text => {
    this.copyTextAndNotify(text, t("citations.citation-copied"));
  };

  copyTextAndNotify(text, success) {
    const result = Fraction.Helpers.copyText(text);
    if (result) {
      toastUtils.notify(success, { type: "success" });
    } else {
      toastUtils.notify(t("errors.copy-failed"), { type: "error" });
    }
  }

  getCitations() {
    const { apa, har, mla, van, abnt, showABNTCitation } = this.props;
    const content = [];
    content.push({
      title: t("citations.mla", "MLA"),
      citationID: mla || i18n.t("base.loading")
    });
    content.push({
      title: t("citations.apa", "APA"),
      citationID: apa || i18n.t("base.loading")
    });
    content.push({
      title: t("citations.harvard", "Harvard"),
      citationID: har || i18n.t("base.loading")
    });
    content.push({
      title: t("citations.vancouver", "Vancouver"),
      citationID: van || i18n.t("base.loading")
    });
    if (showABNTCitation) {
      content.push({
        title: t("citations.abnt", "ABNT"),
        citationID: abnt || i18n.t("base.loading")
      });
    }

    return content;
  }

  renderCitations() {
    const { theme, showCitations } = this.props;
    if (showCitations) {
      const citations = this.getCitations();
      return (
        <BookCitationContainer>
          <CitationHeader>{t("citations.citation", "Citation")}</CitationHeader>
          {citations.map(citation => (
            <StyledAccordion
              onToggle={expanded => {
                Fraction.Reporter.noticeInteraction(
                  expanded
                    ? "library-citations-expand"
                    : "library-citations-collapse",
                  {
                    vbid: this.props.isbn
                  }
                );
              }}
            >
              <StyledAccordionButton level={4}>
                <StyledCaret />
                {citation.title}
              </StyledAccordionButton>
              <AccordionContent>
                <CitationContent
                  dangerouslySetInnerHTML={{ __html: citation.citationID }}
                />
                {Fraction.Helpers.copyEnabled() && (
                  <Button
                    label={t("library.copy-link")}
                    fullWidth
                    color="white"
                    colorBehindButton={theme.color.primary.getShade(7)}
                    tooltipPlacement="bottom"
                    onClick={() => {
                      this.copyCitation(citation.citationID);
                      Fraction.Reporter.noticeInteraction(
                        "library-citations-copy",
                        {
                          vbid: this.props.isbn
                        }
                      );
                    }}
                  >
                    {t("reader.notebook.copy", "Copy")}
                  </Button>
                )}
              </AccordionContent>
            </StyledAccordion>
          ))}
          <WarningMessage>
            <WarningIcon color={theme.color.secondary.getShade(7)} />
            <span>
              {t(
                "citations.warning-message",
                "Always check citation for accuracy before use."
              )}
            </span>
          </WarningMessage>
        </BookCitationContainer>
      );
    }
    return null;
  }

  render() {
    const {
      isbn,
      decks,
      book,
      isLicensedBook,
      highlights,
      theme,
      url
    } = this.props;
    const detailsFooterVisible = !!decks.length || !!highlights;
    const durationMessage = expirationMessage(book);
    return (
      <DetailsContainerOuter>
        <ScrollingContainer hasFooter={detailsFooterVisible}>
          <PanelHeadingTag>{t("library.details", "Details")}</PanelHeadingTag>
          {isLicensedBook && <AvailabilityMessage isbn={isbn} />}
          <BookDetails>
            {book.publisher && (
              <div className="detail-container">
                <span className="detail">
                  {t("library.book.publisher", "Publisher:")}
                </span>
                <span className="detail-output">{book.publisher}</span>
              </div>
            )}
            {book.format && (
              <div className="detail-container">
                <span className="detail">
                  {t("library.book.format", "Format:")}
                </span>
                <span className="detail-output">{book.format}</span>
              </div>
            )}
            {book.published && (
              <div className="detail-container">
                <span className="detail">
                  {t("library.book.published", "Published:")}
                </span>
                <span className="detail-output">{book.published}</span>
              </div>
            )}
            {isbn && (
              <div className="detail-container">
                <span className="detail">
                  {t("library.book.vbid", "VBID:")}
                </span>
                <span className="detail-output">{isbn}</span>
              </div>
            )}
            {durationMessage && (
              <div className="detail-container expiration">
                {durationMessage.Icon && (
                  <durationMessage.Icon outline={cloud.getShade(9)} />
                )}
                {durationMessage.label}
              </div>
            )}
          </BookDetails>
          <BookCopyContainer>
            <CopyURLHeader>{t("library.copy-link", "Copy Link")}</CopyURLHeader>
            <BookCopyURL>
              <CopyURLText>{url}</CopyURLText>
              {Fraction.Helpers.copyEnabled() && (
                <Button
                  label={t("library.copy-link", "Copy Link")}
                  fullWidth
                  color="white"
                  colorBehindButton={theme.color.primary.getShade(7)}
                  tooltipPlacement="bottom"
                  onClick={this.copyLink}
                >
                  {t("reader.notebook.copy", "Copy")}
                </Button>
              )}
            </BookCopyURL>
          </BookCopyContainer>
          {this.renderCitations()}
        </ScrollingContainer>
        {detailsFooterVisible && (
          <Footer decks={decks} highlights={highlights} />
        )}
      </DetailsContainerOuter>
    );
  }
}

const mapStateToProps = (state, props) => ({
  book: booksSelectors.getBookByIsbn(state, props.isbn),
  isLicensedBook: booksSelectors.isLicensedBook(state, props.isbn),
  decks: overleafSelectors.getAllDecks(state, props.isbn),
  highlights: highlightSelectors.getHighlightsSummaryForVbid(state, props.isbn),
  apa: citationSelectors.getCitation(state, props.isbn, "apa"),
  mla: citationSelectors.getCitation(state, props.isbn, "mla"),
  har: citationSelectors.getCitation(state, props.isbn, "har"),
  van: citationSelectors.getCitation(state, props.isbn, "van"),
  abnt: citationSelectors.getCitation(state, props.isbn, "abnt"),
  url:
    document.location.protocol +
    "//" +
    document.location.host +
    "/books/" +
    props.isbn,
  showABNTCitation: brandSelectors.getBrandValue(
    state,
    "Citations.ABNT",
    false
  ),
  showCitations: brandSelectors.getBrandValue(state, "Citations.enabled", true) && brandSelectors.getBrandValue(state, "Citations.libraryEnabled", false)
});

const mapDispatchToProps = (dispatch, props) => ({
  getCitationInfo: () =>
    dispatch(citationActions.getBookCitationInfo(props.isbn)),
  fetchDecks: isbn => dispatch(overleafActions.fetchDecks(isbn)),
  fetchHighlightsSummary: () =>
    dispatch(highlightActions.fetchHighlightsSummary())
});

export default withTheme(
  connect(mapStateToProps, mapDispatchToProps)(BookDetailsContainer)
);
